import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, Button } from '../components/ui';

// window.confirm() is unreliable on several tablet/in-app browsers — some
// never render it, silently returning false so a delete just does nothing
// with no visible error. This is a promise-based drop-in replacement:
// `if (!(await confirm('Delete this?'))) return;` — same shape as the old
// `if (!window.confirm(...)) return;` guard, but backed by a real modal
// that always renders. One dialog, rendered once at the app root, so it
// always sits above any page-level modal regardless of nesting.
const ConfirmContext = createContext(null);

export function ConfirmProvider({ children }) {
  const { t } = useTranslation();
  const [message, setMessage] = useState(null);
  const resolveRef = useRef(null);

  const confirm = useCallback((msg) => {
    return new Promise((resolve) => {
      resolveRef.current = resolve;
      setMessage(msg);
    });
  }, []);

  const settle = (result) => {
    setMessage(null);
    resolveRef.current?.(result);
    resolveRef.current = null;
  };

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <Modal
        open={message !== null}
        onClose={() => settle(false)}
        title={t('Please confirm')}
        size="sm"
        zIndexClass="z-[70]"
        footer={
          <>
            <Button variant="secondary" onClick={() => settle(false)}>{t('Cancel')}</Button>
            <Button variant="danger" onClick={() => settle(true)}>{t('Delete')}</Button>
          </>
        }
      >
        <p className="text-sm text-gray-600">{message}</p>
      </Modal>
    </ConfirmContext.Provider>
  );
}

export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) {
    throw new Error('useConfirm must be used within ConfirmProvider');
  }
  return ctx;
}
